import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { reportRoyalCollectReelReady } from "../../State/actions";
import { selectRoyalCollectSymbolsReady } from "../../State/selectors";
import RoyalCollectSymbol from "./RoyalCollectSymbol";

/**
 * RoyalCollectReel
 *
 * Column container for Royal Collect symbols.
 * Creates GAME_LAYOUT.SYMBOLS_VISIBLE symbol components per reel.
 * Reports readiness to Redux when all symbols are populated.
 */
const RoyalCollectReel = forwardRef(
  (
    {
      reel,
      index,
      x,
      symbolWidth,
      symbolHeight,
      symbolsSpriteSheet,
      symbolsSpriteSheetBlurred,
      reelSymbolsData,
    },
    ref,
  ) => {
    const symbolRefs = useRef([]);
    const reportedReadyRef = useRef(false);
    const dispatch = useDispatch();
    const royalCollectSymbolsReady = useSelector(
      selectRoyalCollectSymbolsReady,
    );

    // Initialize symbol refs array
    useEffect(() => {
      if (symbolRefs.current.length !== reel.length) {
        symbolRefs.current = Array.from({ length: reel.length }, () =>
          React.createRef(),
        );
      }
    }, [reel.length]);

    // Memoize controller methods
    const getSymbolRef = useCallback((symbolIndex) => {
      return symbolRefs.current[symbolIndex];
    }, []);

    const getSymbolRefs = useCallback(() => {
      return symbolRefs.current;
    }, []);

    // Expose imperative API — direct access to symbol refs
    useImperativeHandle(
      ref,
      () => ({
        getSymbolRef,
        getSymbolRefs,
      }),
      [getSymbolRef, getSymbolRefs],
    );

    // Report to Redux when this reel's refs are ready
    useEffect(() => {
      if (!royalCollectSymbolsReady || reportedReadyRef.current) {
        return;
      }

      const allRefsPopulated =
        symbolRefs.current.length === reel.length &&
        symbolRefs.current.every((r) => r && r.current);

      if (allRefsPopulated) {
        dispatch(reportRoyalCollectReelReady());
        reportedReadyRef.current = true;
      }
    }, [royalCollectSymbolsReady, reel.length, index, dispatch]);

    return (
      <Container name={`royal_collect_reel_${index}`} x={x}>
        {reel.map((symbol, arrayIdx) => (
          <RoyalCollectSymbol
            key={`rc_sym_${arrayIdx}`}
            ref={symbolRefs.current[arrayIdx]}
            index={arrayIdx}
            reelIndex={index}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            symbolId={symbol}
            symbolsSpriteSheet={symbolsSpriteSheet}
            symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
            reelSymbolsData={reelSymbolsData}
          />
        ))}
      </Container>
    );
  },
);

RoyalCollectReel.displayName = "RoyalCollectReel";

export default RoyalCollectReel;
